import { useRef, useState, useEffect, useReducer, createElement } from 'react';
import { createState } from '@state-designer/core';
export { S, createDesign, createState } from '@state-designer/core';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it;

  if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) {
    if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
      if (it) o = it;
      var i = 0;
      return function () {
        if (i >= o.length) return {
          done: true
        };
        return {
          done: false,
          value: o[i++]
        };
      };
    }

    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }

  it = o[Symbol.iterator]();
  return it.next.bind(it);
}
/**
 * Create and subscribe to a new state.
 * @param design A designuration object for a new state.
 * @param dependencies (optional) An array of dependencies that, when changed, will rebuild a new state from the provided design.
 */


function useLocalState(design, dependencies) {
  if (dependencies === void 0) {
    dependencies = [];
  }

  var rFirstMount = useRef(true);

  var _React$useState = useState(function () {
    return createState(design);
  }),
      current = _React$useState[0],
      setCurrent = _React$useState[1];

  useEffect(function () {
    function handleUpdate(update) {
      setCurrent(function (current) {
        return _extends({}, current, {
          index: update.index,
          data: update.data,
          active: update.active,
          stateTree: update.stateTree,
          values: update.values,
          log: update.log
        });
      });
    } // Only create a new state if the `design` property is design object.


    if (!rFirstMount.current) {
      var next = createState(design);
      setCurrent(next);
      return next.onUpdate(handleUpdate);
    }

    rFirstMount.current = false;
    return current.onUpdate(handleUpdate); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [].concat(dependencies));
  return current;
}
/**
 * Subscribe a component to a state created with createState.
 * @param design A state returned from createState.
 */


function useGlobalState(design) {
  var _React$useState = useState(function () {
    return design;
  }),
      current = _React$useState[0],
      setCurrent = _React$useState[1];

  useEffect(function () {
    setCurrent(design);
    return design.onUpdate(function (update) {
      return setCurrent(function (current) {
        return _extends({}, current, {
          index: update.index,
          data: update.data,
          active: update.active,
          stateTree: update.stateTree,
          values: update.values,
          log: update.log
        });
      });
    });
  }, [design]);
  return current;
}

var emptyArray = [];

function useStateDesigner(design, dependencies) {
  if (dependencies === void 0) {
    dependencies = emptyArray;
  }

  var rFirstMount = useRef(true);

  var _React$useState = useState(function () {
    return 'active' in design ? design : createState(design);
  }),
      current = _React$useState[0],
      setCurrent = _React$useState[1]; // Global


  useEffect(function () {
    if (!('active' in design)) return;
    setCurrent(design);
    return design.onUpdate(function (update) {
      return setCurrent(function (current) {
        return _extends({}, current, {
          index: update.index,
          data: update.data,
          active: update.active,
          stateTree: update.stateTree,
          values: update.values,
          log: update.log
        });
      });
    });
  }, [design]); // Local

  useEffect(function () {
    if ('active' in design) return;

    function handleUpdate(update) {
      setCurrent(function (current) {
        return _extends({}, current, {
          index: update.index,
          data: update.data,
          active: update.active,
          stateTree: update.stateTree,
          values: update.values,
          log: update.log
        });
      });
    } // Only create a new state if the `design` property is design object.


    if (!rFirstMount.current) {
      var next = createState(design);
      setCurrent(next);
      return next.onUpdate(handleUpdate);
    }

    rFirstMount.current = false;
    return current.onUpdate(handleUpdate);
  }, [].concat(dependencies));
  return current;
}
/**
 * useSelector
 * @param state A state created with `createState`.
 * @param selectFn A function that returns information derived from a state update.
 * @param compareFn - (Optional) A function to test whether the new selection matches the old section.
 * @example const tables = useSelector(state, (update) => update.data.tables)
 */


function useSelector(state, selectFn, compareFn) {
  if (compareFn === void 0) {
    compareFn = function compareFn(prev, next) {
      return prev === next;
    };
  }

  var _useReducer = useReducer(function (state, update) {
    var next = selectFn(update);
    return compareFn(state.current, next) ? state : {
      count: state.count + 1,
      current: next
    };
  }, state, function (state) {
    return {
      count: 0,
      current: selectFn(state)
    };
  }),
      inner = _useReducer[0],
      dispatch = _useReducer[1];

  useEffect(function () {
    return state.onUpdate(function (update) {
      return dispatch(update);
    });
  }, [state, dispatch]);
  return inner.current;
}
/**
 * createSelectorHook
 * @description A function that returns a useSelector hook for the
 * given state. The hook works just like `useSelector`, except that
 * you do not need to pass the hook a state as its first argument.
 * @param state A state created with `createState`.
 * @example const useSelector = createSelectorHook(roomState)
 *
 * // In a component
 * const tables = useSelector((state) => state.data.tables)
 */


function createSelectorHook(state) {
  return function useSelector(selectFn, compareFn) {
    if (compareFn === void 0) {
      compareFn = function compareFn(prev, next) {
        return prev === next;
      };
    }

    var _useReducer = useReducer(function (state, update) {
      var next = selectFn(update);
      return compareFn(state.current, next) ? state : {
        count: state.count + 1,
        current: next
      };
    }, state, function (state) {
      return {
        count: 0,
        current: selectFn(state)
      };
    }),
        inner = _useReducer[0],
        dispatch = _useReducer[1];

    useEffect(function () {
      return state.onUpdate(function (update) {
        return dispatch(update);
      });
    }, [dispatch]);
    return inner.current;
  };
}

var EventList = function EventList(_ref) {
  var state = _ref.state;

  function getEvents(state) {
    var localEvents = [];

    if (state.active) {
      localEvents.push.apply(localEvents, Object.keys(state.on));
    }

    for (var _i = 0, _Object$values = Object.values(state.states); _i < _Object$values.length; _i++) {
      var child = _Object$values[_i];
      localEvents.push.apply(localEvents, getEvents(child));
    }

    return localEvents;
  }

  var eventMap = new Map([]);

  for (var _iterator = _createForOfIteratorHelperLoose(getEvents(state)), _step; !(_step = _iterator()).done;) {
    var event = _step.value;
    var prior = eventMap.get(event);

    if (prior === undefined) {
      eventMap.set(event, 1);
    } else {
      eventMap.set(event, prior + 1);
    }
  }

  return createElement("ul", {
    className: "list event"
  }, Array.from(eventMap.entries()).map(function (_ref2, i) {
    var eventName = _ref2[0],
        count = _ref2[1];
    return createElement("li", {
      key: i,
      className: "item event"
    }, eventName, count > 1 && " x" + count);
  }));
};

var StateNode = function StateNode(_ref3) {
  var state = _ref3.state;
  var active = state.active;
  var states = Object.values(state.states);
  return createElement("li", {
    "data-active": active,
    className: "item state"
  }, active ? createElement("b", null, state.name) : state.name, states.length > 0 && createElement("ul", {
    className: "list state"
  }, states.map(function (child, i) {
    return createElement(StateNode, {
      key: i,
      state: child
    });
  })));
};

var StateGraph = function StateGraph(_ref4) {
  var state = _ref4.state,
      rest = _objectWithoutPropertiesLoose(_ref4, ["state"]);

  var _useStateDesigner = useStateDesigner(state, [state]),
      stateTree = _useStateDesigner.stateTree;

  return createElement("div", Object.assign({}, rest), createElement("span", {
    className: "title states"
  }, "States:"), createElement("ul", null, createElement(StateNode, {
    state: stateTree
  })), createElement("span", {
    className: "title events"
  }, "Events:"), createElement(EventList, {
    state: stateTree
  }));
};

export { StateGraph, createSelectorHook, useGlobalState, useLocalState, useSelector, useStateDesigner };